import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  X,
  Loader2,
  Plus,
  Code,
  Eye,
  Info,
  ChevronRight,
  Search,
  Table,
  BarChart2,
  Hash,
  Gauge,
  PieChart,
  Disc,
  AlignLeft,
  LineChart,
  AreaChart,
  Grid3X3,
  AlertTriangle,
} from "lucide-react";
import baseapi from "../../services/baseapi";
import { showError, showSuccess } from "../../utils/toast";
import Chart from "react-apexcharts";

// ─── Constants ───
const CHART_ICONS: Record<string, React.ComponentType<any>> = {
  metric: Hash,
  gauge: Gauge,
  pie: PieChart,
  donut: Disc,
  bar: BarChart2,
  horizontal_bar: AlignLeft,
  line: LineChart,
  area: AreaChart,
  heatmap: Grid3X3,
  table: Table,
};

const CHART_COLORS: Record<string, string> = {
  metric: "text-blue-400",
  gauge: "text-emerald-400",
  pie: "text-pink-400",
  donut: "text-purple-400",
  bar: "text-orange-400",
  horizontal_bar: "text-amber-400",
  line: "text-sky-400",
  area: "text-teal-400",
  heatmap: "text-red-400",
  table: "text-indigo-400",
};

const AGG_LABELS: Record<string, string> = {
  count: "Count of rows",
  distinct_count: "Unique count",
  sum: "Sum",
  avg: "Average",
  min: "Minimum",
  max: "Maximum",
  median: "Median",
  percentiles: "Percentiles",
  std_deviation: "Std deviation",
};

const OP_LABELS: Record<string, string> = {
  eq: "is",
  ne: "is not",
  contains: "contains",
  not_contains: "doesn't contain",
  starts_with: "starts with",
  ends_with: "ends with",
  gt: ">",
  gte: "≥",
  lt: "<",
  lte: "≤",
  in: "is one of",
  not_in: "is not one of",
  between: "between",
  exists: "has a value",
  not_exists: "is empty",
  has: "includes",
  not_has: "doesn't include",
};

// ─── Custom Select Component ───
function CustomSelect({
  value,
  options,
  onChange,
  placeholder = "Select option...",
}: {
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (val: string) => void;
  placeholder?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const clickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", clickOutside);
    return () => document.removeEventListener("mousedown", clickOutside);
  }, []);

  const selectedOption = options.find((o) => o.value === value);

  return (
    <div className="relative flex-1" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-gray-950 border border-gray-700 hover:border-gray-600 text-left text-xs text-white px-3 py-2 rounded-lg font-semibold truncate flex items-center justify-between"
      >
        <span className="truncate">{selectedOption ? selectedOption.label : placeholder}</span>
        <ChevronRight className="w-3.5 h-3.5 text-gray-500 shrink-0 rotate-90" />
      </button>

      {isOpen && (
        <div className="absolute top-[105%] left-0 right-0 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-[999] max-h-[220px] overflow-y-auto custom-scroll p-1 flex flex-col gap-0.5">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => {
                onChange(o.value);
                setIsOpen(false);
              }}
              className={`w-full text-left text-xs px-3 py-1.5 rounded transition-colors ${
                o.value === value ? "bg-orange-500/10 text-orange-400 font-bold" : "text-gray-300 hover:bg-gray-800"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Field Picker Drill Interface ───
interface FieldPickerDrill {
  source: string;
  prefix: string;
  baseField: string;
  path: string;
  isArray: boolean;
  parent: FieldPickerDrill | null;
}

function FieldPickerComponent({
  getSourcesList,
  value,
  onPick,
  placeholder = "Select field column...",
  roleFilter = null,
}: {
  getSourcesList: () => any[];
  value?: string;
  onPick: (v: string) => void;
  placeholder?: string;
  roleFilter?: ((f: any) => boolean) | null;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [drill, setDrill] = useState<FieldPickerDrill | null>(null);

  const [jsonKeys, setJsonKeys] = useState<string[]>([]);
  const [jsonIsArray, setJsonIsArray] = useState(false);
  const [loadingKeys, setLoadingKeys] = useState(false);

  const [sourceFields, setSourceFields] = useState<Record<string, any[]>>({});
  const [loadingFields, setLoadingFields] = useState(false);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setDrill(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const fetchSourceFields = async () => {
    setLoadingFields(true);
    try {
      const entries = getSourcesList();
      const fieldsMap: Record<string, any[]> = {};
      await Promise.all(
        entries.map(async (src) => {
          try {
            const res = await baseapi.get(`/cdash/meta/sources/${src.source}/fields`);
            fieldsMap[src.source] = res.data;
          } catch {
            fieldsMap[src.source] = [];
          }
        })
      );
      setSourceFields(fieldsMap);
    } catch {
      // ignore
    } finally {
      setLoadingFields(false);
    }
  };

  useEffect(() => {
    if (isOpen && !drill) {
      fetchSourceFields();
    }
  }, [isOpen, drill]);

  useEffect(() => {
    if (!drill) return;
    const fetchKeys = async () => {
      setLoadingKeys(true);
      try {
        const res = await baseapi.get(`/cdash/meta/sources/${drill.source}/jsonb/${drill.baseField}`, {
          params: { path: drill.path },
        });
        setJsonKeys(res.data?.keys || []);
        setJsonIsArray(!!res.data?.is_array);
      } catch {
        setJsonKeys([]);
        setJsonIsArray(false);
      } finally {
        setLoadingKeys(false);
      }
    };
    fetchKeys();
  }, [drill]);

  const handleSelect = (fullPath: string) => {
    onPick(fullPath);
    setIsOpen(false);
    setDrill(null);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-gray-805 border border-gray-700 hover:border-gray-600 text-left text-xs text-white px-3 py-2 rounded-lg font-semibold truncate flex items-center justify-between"
      >
        <span className="truncate">{value || placeholder}</span>
        <ChevronRight className="w-3.5 h-3.5 text-gray-500 shrink-0 rotate-90" />
      </button>

      {isOpen && (
        <div className="absolute top-[105%] left-0 right-0 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-[999] max-h-[300px] overflow-y-auto custom-scroll p-2 flex flex-col gap-1">
          {drill ? (
            <div className="flex flex-col gap-1">
              <button
                type="button"
                onClick={() => setDrill(drill.parent)}
                className="w-full text-left text-[10px] text-gray-400 hover:text-white px-2 py-1 rounded bg-gray-800 flex items-center gap-1.5 font-bold"
              >
                ← Back
              </button>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider px-2 py-1 select-none font-bold">
                Drill: {drill.baseField}
                {drill.path ? `.${drill.path}` : ""}
                {jsonIsArray ? " [List]" : ""}
              </div>
              <button
                type="button"
                onClick={() => handleSelect(drill.prefix + drill.baseField + (drill.path ? `.${drill.path}` : ""))}
                className="w-full text-left text-xs text-orange-400 hover:bg-gray-800 px-2.5 py-1.5 rounded font-bold"
              >
                Use this JSON path
              </button>

              {loadingKeys ? (
                <div className="flex items-center gap-2 px-3 py-2 text-xs text-gray-500">
                  <Loader2 className="w-3 h-3 animate-spin text-orange-500" /> Fetching keys...
                </div>
              ) : jsonKeys.length === 0 ? (
                <div className="text-[10px] text-gray-500 px-3 py-2 font-medium italic">No nested keys found in logs.</div>
              ) : (
                jsonKeys.map((key) => {
                  const segment = jsonIsArray ? `0.${key}` : key;
                  const nextPath = drill.path ? `${drill.path}.${segment}` : segment;

                  return (
                    <div key={key} className="flex items-center justify-between group hover:bg-gray-800 rounded">
                      <button
                        type="button"
                        onClick={() => handleSelect(drill.prefix + drill.baseField + `.${nextPath}`)}
                        className="flex-1 text-left text-xs text-gray-300 px-2.5 py-1.5 rounded truncate"
                      >
                        {key}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setDrill({
                            source: drill.source,
                            prefix: drill.prefix,
                            baseField: drill.baseField,
                            path: nextPath,
                            isArray: false,
                            parent: drill,
                          })
                        }
                        className="p-1 px-2.5 text-gray-500 hover:text-white rounded border border-transparent hover:border-gray-700 text-[11px]"
                      >
                        ›
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {loadingFields ? (
                <div className="flex items-center justify-center p-6 gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                  <span className="text-xs text-gray-500">Fetching schema columns...</span>
                </div>
              ) : (
                getSourcesList().map((src) => {
                  const fields = sourceFields[src.source] || [];
                  return (
                    <div key={src.source} className="flex flex-col gap-1">
                      <div className="text-[9px] uppercase tracking-wider text-orange-500 font-bold px-2.5 pt-2 pb-1 bg-gray-850 border-b border-gray-700 select-none">
                        {src.label}
                      </div>
                      {fields
                        .filter((f) => !roleFilter || f.is_jsonb || roleFilter(f))
                        .map((f) => (
                          <button
                            key={f.name}
                            type="button"
                            onClick={() => {
                              if (f.is_jsonb) {
                                setDrill({
                                  source: src.source,
                                  prefix: src.prefix,
                                  baseField: f.name,
                                  path: "",
                                  isArray: false,
                                  parent: null,
                                });
                              } else {
                                handleSelect(src.prefix + f.name);
                              }
                            }}
                            className="w-full text-left text-xs text-gray-300 hover:bg-gray-800 px-3 py-1.5 rounded flex justify-between items-center font-medium"
                          >
                            <span className="truncate">{f.label || f.name}</span>
                            <span className="text-[9px] text-gray-500 select-none font-bold uppercase">
                              {f.is_jsonb ? "JSON ›" : f.type}
                            </span>
                          </button>
                        ))}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Filter Source and Field Helper ───
function getFilterSourceAndField(fieldPath: string, queryDef: any) {
  if (!fieldPath) return { source: "", field: "" };
  let source = queryDef.source || "";
  let field = fieldPath;

  if (queryDef.joins?.length) {
    for (const j of queryDef.joins) {
      if (fieldPath.startsWith(j.source + ".")) {
        source = j.source;
        field = fieldPath.slice(j.source.length + 1);
        break;
      }
    }
  }
  return { source, field };
}

interface FilterValueInputProps {
  source: string;
  field: string;
  operator: string;
  value: any;
  onChange: (val: any) => void;
}

function FilterValueInput({ source, field, operator, value, onChange }: FilterValueInputProps) {
  const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [filterText, setFilterText] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const fetchValues = async () => {
    if (!source || !field) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      const storedLevel = localStorage.getItem("level");
      const levelObj = storedLevel ? JSON.parse(storedLevel) : null;
      const params: any = {};
      if (levelObj && levelObj.name !== "global" && levelObj.id) {
        params.client_id = levelObj.id;
      }

      const res = await baseapi.get(`/cdash/meta/sources/${source}/fields/${field}/values`, { params });
      let raw: any[] = [];
      if (Array.isArray(res.data)) {
        raw = res.data;
      } else if (res.data && Array.isArray(res.data.values)) {
        raw = res.data.values;
      } else if (res.data && Array.isArray(res.data.data)) {
        raw = res.data.data;
      }

      const formatted = raw.map((item: any) => {
        if (item && typeof item === "object") {
          const val = item.value ?? item.id ?? item.name ?? String(item);
          const lbl = item.label ?? item.name ?? item.title ?? String(val);
          return { value: String(val), label: String(lbl) };
        }
        return { value: String(item), label: String(item) };
      });
      setOptions(formatted);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && source && field) {
      fetchValues();
    }
  }, [isOpen, source, field]);

  const isMulti = ["in", "not_in", "between"].includes(operator);
  const displayVal = Array.isArray(value) ? value.join(", ") : value ?? "";

  const filteredOptions = useMemo(() => {
    if (!filterText.trim()) return options;
    const lower = filterText.toLowerCase();
    return options.filter(
      (o) => o.label.toLowerCase().includes(lower) || o.value.toLowerCase().includes(lower)
    );
  }, [options, filterText]);

  const handleSelectOption = (optVal: string) => {
    if (isMulti) {
      const currentArray = Array.isArray(value)
        ? value
        : typeof value === "string" && value.trim()
        ? value.split(",").map((s) => s.trim()).filter(Boolean)
        : [];
      const nextArray = currentArray.includes(optVal)
        ? currentArray.filter((v: string) => v !== optVal)
        : [...currentArray, optVal];
      onChange(nextArray);
    } else {
      onChange(optVal);
      setIsOpen(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setFilterText(text);
    if (isMulti) {
      const arr = text.split(",").map((s) => s.trim()).filter(Boolean);
      onChange(arr);
    } else {
      onChange(text);
    }
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div className="relative flex items-center">
        <input
          type="text"
          placeholder={isMulti ? "value1, value2, value3" : "value..."}
          value={displayVal}
          onFocus={() => {
            setIsOpen(true);
            setFilterText("");
          }}
          onChange={handleInputChange}
          className="w-full bg-gray-855 border border-gray-700 hover:border-gray-700 rounded-lg text-xs text-white px-3 py-1.5 focus:outline-none focus:border-orange-500 pr-8"
        />
        <button
          type="button"
          onClick={() => {
            if (!isOpen && source && field) fetchValues();
            setIsOpen(!isOpen);
          }}
          className="absolute right-2 text-gray-500 hover:text-white p-0.5"
        >
          {loading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-400" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 rotate-90" />
          )}
        </button>
      </div>

      {isOpen && (
        <div className="absolute top-[105%] left-0 right-0 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-[999] max-h-[200px] overflow-y-auto custom-scroll p-1 flex flex-col gap-0.5">
          {loading && (
            <div className="flex items-center justify-center py-3 text-xs text-gray-400 gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-400" />
              <span>Fetching values...</span>
            </div>
          )}

          {!loading && filteredOptions.length === 0 && (
            <div className="py-2.5 px-3 text-xs text-gray-500 text-center italic">
              {source && field ? "No predefined values found. Type custom value." : "Select a field first"}
            </div>
          )}

          {!loading &&
            filteredOptions.map((o) => {
              const selected = isMulti
                ? Array.isArray(value) && value.includes(o.value)
                : value === o.value;
              return (
                <button
                  type="button"
                  key={o.value}
                  onClick={() => handleSelectOption(o.value)}
                  className={`w-full text-left text-xs px-3 py-1.5 rounded transition-colors flex items-center justify-between ${
                    selected
                      ? "bg-orange-500/10 text-orange-400 font-bold"
                      : "text-gray-300 hover:bg-gray-800"
                  }`}
                >
                  <span className="truncate">{o.label}</span>
                  {selected && <span className="text-orange-400 font-bold text-xs">✓</span>}
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
}

// ─── Multi-Column Selector Component for Raw Table Mode ───
function MultiColumnSelector({
  getSourcesList,
  selectedColumns,
  onChange,
}: {
  getSourcesList: () => Array<{ source: string; label: string; prefix: string }>;
  selectedColumns: string[];
  onChange: (cols: string[]) => void;
}) {
  const [fields, setFields] = useState<
    Array<{
      key: string;
      label: string;
      displayLabel: string;
      source: string;
      sourceLabel: string;
      type: string;
      isJoined: boolean;
    }>
  >([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const sourcesList = getSourcesList();
  const sourcesKey = sourcesList.map((s) => s.source).join(",");

  useEffect(() => {
    const fetchAllFields = async () => {
      if (!sourcesList.length) return;
      setLoading(true);
      try {
        const fieldResults: Array<{
          key: string;
          label: string;
          displayLabel: string;
          source: string;
          sourceLabel: string;
          type: string;
          isJoined: boolean;
        }> = [];

        await Promise.all(
          sourcesList.map(async (srcInfo, index) => {
            try {
              const res = await baseapi.get(`/cdash/meta/sources/${srcInfo.source}/fields`);
              const items = Array.isArray(res.data) ? res.data : [];
              const isJoined = index > 0;
              items.forEach((f: any) => {
                const key = isJoined ? `${srcInfo.source}.${f.name}` : f.name;
                const displayLabel = isJoined ? `${srcInfo.label} → ${f.label || f.name}` : (f.label || f.name);
                fieldResults.push({
                  key,
                  label: f.label || f.name,
                  displayLabel,
                  source: srcInfo.source,
                  sourceLabel: srcInfo.label,
                  type: f.type || "string",
                  isJoined,
                });
              });
            } catch {
              // ignore
            }
          })
        );
        setFields(fieldResults);
      } finally {
        setLoading(false);
      }
    };

    fetchAllFields();
  }, [sourcesKey]);

  useEffect(() => {
    const clickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", clickOutside);
    return () => document.removeEventListener("mousedown", clickOutside);
  }, []);

  const toggleColumn = (colKey: string) => {
    if (selectedColumns.includes(colKey)) {
      onChange(selectedColumns.filter((c) => c !== colKey));
    } else {
      onChange([...selectedColumns, colKey]);
    }
  };

  const filteredFields = useMemo(() => {
    if (!search.trim()) return fields;
    const q = search.toLowerCase();
    return fields.filter(
      (f) =>
        f.key.toLowerCase().includes(q) ||
        f.label.toLowerCase().includes(q) ||
        f.sourceLabel.toLowerCase().includes(q)
    );
  }, [fields, search]);

  const handleSelectAll = () => {
    const filteredKeys = filteredFields.map((f) => f.key);
    const combined = Array.from(new Set([...selectedColumns, ...filteredKeys]));
    onChange(combined);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex flex-col gap-3" ref={dropdownRef}>
      <div className="flex items-center justify-between">
        <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-450 flex items-center gap-2">
          <span>3 · Table Columns</span>
          <span className="bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-full text-[10px] font-semibold border border-orange-500/30">
            {selectedColumns.length} Selected
          </span>
        </h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSelectAll}
            className="text-[10px] text-orange-400 hover:text-orange-300 font-bold hover:underline"
          >
            ＋ Select All
          </button>
          <span className="text-gray-700">|</span>
          <button
            type="button"
            onClick={handleClearAll}
            className="text-[10px] text-gray-400 hover:text-red-400 font-semibold"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Selected column chips */}
      {selectedColumns.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 p-2 bg-gray-950/60 border border-gray-800 rounded-lg max-h-[140px] overflow-y-auto custom-scroll">
          {selectedColumns.map((colKey) => {
            const fieldObj = fields.find((f) => f.key === colKey);
            return (
              <span
                key={colKey}
                className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-800 text-gray-200 border border-gray-700 rounded-md text-[11px] font-medium group hover:border-orange-500/50 transition-colors"
              >
                {fieldObj?.isJoined && (
                  <span className="text-[9px] px-1 py-0.2 bg-purple-500/20 text-purple-300 rounded border border-purple-500/30 font-semibold">
                    {fieldObj.sourceLabel}
                  </span>
                )}
                <span>{fieldObj ? fieldObj.label : colKey}</span>
                <button
                  type="button"
                  onClick={() => toggleColumn(colKey)}
                  className="text-gray-500 group-hover:text-red-400 hover:bg-gray-700 rounded p-0.5 ml-0.5 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      ) : (
        <div className="text-[11px] text-gray-500 italic p-3 bg-gray-950/40 border border-dashed border-gray-800 rounded-lg text-center">
          No columns selected. Pick fields from dropdown below or click "Select All" to display in table.
        </div>
      )}

      {/* Multi Select Dropdown */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="w-full bg-gray-950 border border-gray-700 hover:border-gray-600 text-left text-xs text-white px-3 py-2.5 rounded-lg font-semibold flex items-center justify-between transition-colors"
        >
          <span className="text-gray-300 truncate">
            {loading ? "Loading available fields..." : "＋ Add / Remove Table Columns..."}
          </span>
          <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${dropdownOpen ? "rotate-90" : ""}`} />
        </button>

        {dropdownOpen && (
          <div className="absolute top-[105%] left-0 right-0 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-[999] max-h-[280px] flex flex-col p-2 gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2.5" />
              <input
                type="text"
                placeholder="Search fields by name or label..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-gray-950 border border-gray-700 rounded-lg text-xs text-white pl-8 pr-3 py-1.5 focus:outline-none focus:border-orange-500 font-medium"
              />
            </div>

            <div className="overflow-y-auto custom-scroll flex-1 flex flex-col gap-1 pr-1">
              {loading ? (
                <div className="p-4 text-center text-xs text-gray-500 flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-orange-400" />
                  Loading field schema...
                </div>
              ) : filteredFields.length === 0 ? (
                <div className="p-3 text-center text-xs text-gray-500">No matching fields found</div>
              ) : (
                filteredFields.map((f) => {
                  const isSelected = selectedColumns.includes(f.key);
                  return (
                    <label
                      key={f.key}
                      className={`flex items-center justify-between px-3 py-1.5 rounded-lg border text-xs cursor-pointer select-none transition-colors ${
                        isSelected
                          ? "bg-orange-500/10 border-orange-500/40 text-orange-300 font-semibold"
                          : "border-transparent text-gray-300 hover:bg-gray-800/70"
                      }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleColumn(f.key)}
                          className="rounded border-gray-700 bg-gray-950 text-orange-500 focus:ring-0 focus:ring-offset-0 w-3.5 h-3.5 cursor-pointer accent-orange-500"
                        />
                        <div className="flex flex-col min-w-0">
                          <span className="truncate text-white font-medium">{f.displayLabel}</span>
                          <span className="text-[10px] text-gray-500 font-mono truncate">{f.key}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {f.isJoined && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 border border-purple-500/30">
                            {f.sourceLabel}
                          </span>
                        )}
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 border border-gray-700">
                          {f.type}
                        </span>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {selectedColumns.length > 5 && (
        <div className="flex items-start gap-2 p-2.5 bg-amber-500/10 border border-amber-500/30 rounded-lg text-[11px] text-amber-200 animate-in fade-in duration-150">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <span>
            <strong>Layout Tip:</strong> You have selected {selectedColumns.length} columns. For PDF reports and A4 pages, having more than 5 columns may compress cell text. Consider keeping key columns for the cleanest readability.
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Interfaces ───
export interface AddWidgetPayload {
  name: string;
  title: string;
  chart_type: string;
  widget_type: string;
  query_definition: any;
  visualization_config: any;
  previewResult?: any;
  chartType?: string;
  chartTitle?: string;
  dataSource?: string;
  dataset_name?: string;
  metric_field?: string;
  aggregation?: string;
  xAxisField?: string;
  group_by_field?: string;
  dimensions?: string[];
  group_by_fields?: string[];
  time_field?: string | null;
  time_granularity?: string | null;
  filters?: any;
  custom_options?: any;
  [key: string]: any;
}

export interface AddWidgetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: AddWidgetPayload) => Promise<void> | void;
  initialData?: any;
}

function ApexChartModalPreview({
  chartType,
  queryResult,
  xAxisLabel = '',
  yAxisLabel = '',
  gaugeLabel = '',
}: {
  chartType: string;
  queryResult: any;
  xAxisLabel?: string;
  yAxisLabel?: string;
  gaugeLabel?: string;
}) {
  const rawRows: any[] = Array.isArray(queryResult?.rows) ? queryResult.rows : [];
  const rawColumns: string[] = Array.isArray(queryResult?.columns) ? queryResult.columns : [];

  const colors = ['#f97316', '#38bdf8', '#818cf8', '#34d399', '#f43f5e', '#fbbf24', '#a855f7', '#06b6d4'];

  if (chartType === 'metric') {
    const totalVal = queryResult?.unfiltered_total !== undefined
      ? queryResult.unfiltered_total
      : (rawRows.length > 0 ? (rawRows[0][1] ?? rawRows[0][0] ?? 0) : 0);

    return (
      <div className="flex flex-col items-center justify-center h-full p-4">
        <span className="text-4xl font-black text-orange-500 tracking-tight">
          {typeof totalVal === 'number' ? totalVal.toLocaleString() : totalVal}
        </span>
        <span className="text-xs font-semibold text-gray-400 mt-2 uppercase tracking-wider">
          {yAxisLabel || gaugeLabel || 'Total Count'}
        </span>
      </div>
    );
  }

  if (chartType === 'table') {
    const tableCols = rawColumns.length > 0 ? rawColumns : ['Dimension', 'Value'];
    const colCount = tableCols.length;
    const isDense = colCount > 8;
    const isMedium = colCount > 5;
    const fontSize = isDense ? '9.5px' : (isMedium ? '10px' : '11px');
    const padding = isDense ? '4px 6px' : (isMedium ? '6px 8px' : '8px');

    return (
      <div className="w-full h-full overflow-hidden text-xs bg-gray-900/60 rounded-lg border border-gray-800">
        <table style={{ width: '100%', maxWidth: '100%', tableLayout: 'fixed' }} className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-gray-800/80 border-b border-gray-700 sticky top-0 z-10">
              {tableCols.map((col, idx) => (
                <th key={idx} style={{ padding, fontSize, width: `${(100 / colCount).toFixed(2)}%` }} className="font-bold text-gray-300 capitalize whitespace-normal break-words leading-tight">
                  {col.replace(/_/g, ' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rawRows.length > 0 ? (
              rawRows.map((row, rIdx) => (
                <tr key={rIdx} className="border-b border-gray-800/60 hover:bg-gray-800/40">
                  {tableCols.map((_, cIdx) => (
                    <td key={cIdx} style={{ padding, fontSize }} className={`whitespace-normal break-words leading-tight ${cIdx === 0 ? 'text-gray-300 font-medium' : 'text-orange-400 font-bold'}`}>
                      {row[cIdx] === null || row[cIdx] === undefined ? '—' : String(row[cIdx])}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={tableCols.length} className="p-4 text-center text-gray-500 italic">
                  No data returned
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }

  const isPie = chartType === 'pie';
  const isDonut = chartType === 'donut';
  const isPieOrDonut = isPie || isDonut;
  const isGauge = chartType === 'gauge';
  const isHorizontal = chartType === 'horizontal_bar';
  const isHeatmap = chartType === 'heatmap';
  const isNonAxis = isPieOrDonut || isGauge;

  const apexType: any = isHorizontal
    ? 'bar'
    : isGauge
      ? 'radialBar'
      : isPie
        ? 'pie'
        : isDonut
          ? 'donut'
          : (['bar', 'line', 'area', 'heatmap'].includes(chartType) ? chartType : 'bar');

  const isScalar = rawColumns.length <= 1 && (rawRows.length === 0 || rawRows[0]?.length <= 1);

  let categories: string[] = [];
  let series: any = [];
  let labels: string[] | undefined = undefined;

  if (isNonAxis) {
    if (isGauge) {
      const val = rawRows.length > 0 ? Number(rawRows[0]?.[1] ?? rawRows[0]?.[0] ?? 74) : 74;
      series = [Math.min(100, Math.max(0, isNaN(val) ? 74 : val))];
      labels = [gaugeLabel || yAxisLabel || 'Score'];
    } else {
      if (rawRows.length > 0) {
        categories = rawRows.map((r: any) => (r[0] === null || r[0] === undefined || r[0] === '' ? '(Unassigned)' : String(r[0])));
        series = rawRows.map((r: any) => {
          const v = Number(r[1] ?? r[0] ?? 0);
          return isNaN(v) ? 0 : v;
        });
      } else {
        categories = ['Category A', 'Category B', 'Category C'];
        series = [45, 30, 25];
      }
      labels = categories;
    }
  } else {
    // Axis charts: Bar, Horizontal Bar, Line, Area, Heatmap
    if (rawRows.length === 0) {
      categories = ['No Data'];
      series = [{ name: yAxisLabel || 'Count', data: [0] }];
    } else if (isScalar) {
      categories = [rawColumns[0] || 'Total'];
      const val = Number(rawRows[0]?.[0] ?? 0);
      series = [{ name: rawColumns[0] || 'Total', data: [isNaN(val) ? 0 : val] }];
    } else if (isHeatmap) {
      categories = rawRows.map((r: any) => (r[0] === null || r[0] === undefined || r[0] === '' ? '(Unassigned)' : String(r[0])));
      series = [{
        name: yAxisLabel || 'Metric',
        data: rawRows.map((r: any) => ({
          x: r[0] === null || r[0] === undefined || r[0] === '' ? '(Unassigned)' : String(r[0]),
          y: Number(r[1] ?? 0)
        }))
      }];
    } else {
      categories = rawRows.map((r: any) => (r[0] === null || r[0] === undefined || r[0] === '' ? '(Unassigned)' : String(r[0])));
      const metricLabel = yAxisLabel || rawColumns[1] || 'Count';
      const seriesData = rawRows.map((r: any) => {
        const v = Number(r[1] ?? r[0] ?? 0);
        return isNaN(v) ? 0 : v;
      });
      series = [{ name: metricLabel, data: seriesData }];
    }
  }

  // Base Apex Options
  const apexOptions: any = {
    chart: {
      type: apexType,
      toolbar: { show: false },
      animations: { enabled: false },
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    colors,
    stroke: {
      curve: 'smooth',
      width: chartType === 'line' || chartType === 'area' ? 2.5 : (isNonAxis ? 1 : 0),
    },
    legend: {
      show: true,
      position: isNonAxis ? 'bottom' : 'top',
      horizontalAlign: isNonAxis ? 'center' : 'right',
      fontSize: '10.5px',
      fontWeight: 500,
      labels: { colors: '#94a3b8' },
      markers: { size: 4 },
      itemMargin: { horizontal: 6, vertical: 2 },
      formatter: (seriesName: string, opts: any) => {
        if (isPieOrDonut && opts?.w?.globals?.series) {
          const val = opts.w.globals.series[opts.seriesIndex];
          if (val !== undefined && val !== null) {
            return `${seriesName}: ${typeof val === 'number' ? val.toLocaleString() : val}`;
          }
        }
        return seriesName;
      },
    },
    dataLabels: {
      enabled: isPieOrDonut,
      formatter: (val: any, opts: any) => {
        if (isPieOrDonut) {
          const count = opts?.w?.config?.series?.[opts?.seriesIndex] ?? opts?.w?.globals?.series?.[opts?.seriesIndex];
          if (count !== undefined && count !== null) {
            return `${typeof count === 'number' ? count.toLocaleString() : count}`;
          }
        }
        return typeof val === 'number' ? val.toLocaleString() : val;
      },
      style: { fontSize: '10px', fontWeight: 600 },
      dropShadow: { enabled: false },
    },
    tooltip: {
      theme: 'dark',
      style: { fontSize: '11px' },
      y: {
        formatter: (val: any) => (typeof val === 'number' ? val.toLocaleString() : val),
      },
    },
  };

  if (isNonAxis) {
    if (labels) {
      apexOptions.labels = labels;
    }
    if (isDonut) {
      apexOptions.plotOptions = {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total',
                fontSize: '11px',
                fontWeight: 600,
                color: '#94a3b8',
                formatter: (w: any) => {
                  const total = w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0);
                  return typeof total === 'number' ? total.toLocaleString() : total;
                },
              },
            },
          },
        },
      };
    } else if (isPie) {
      apexOptions.plotOptions = {
        pie: {
          expandOnClick: true,
        },
      };
    } else if (isGauge) {
      apexOptions.plotOptions = {
        radialBar: {
          startAngle: -135,
          endAngle: 135,
          hollow: { size: '60%' },
          dataLabels: {
            name: { show: true, fontSize: '11px', color: '#94a3b8' },
            value: { show: true, fontSize: '20px', fontWeight: 700, color: '#f97316' },
          },
        },
      };
    }
  } else {
    // Axis charts ONLY
    apexOptions.grid = {
      show: true,
      borderColor: '#334155',
      strokeDashArray: 3,
    };
    if (apexType === 'bar') {
      apexOptions.plotOptions = {
        bar: {
          horizontal: isHorizontal,
          borderRadius: 3,
          columnWidth: '55%',
        },
      };
    }
    apexOptions.xaxis = {
      categories,
      title: {
        text: isHorizontal ? (yAxisLabel || '') : (xAxisLabel || ''),
        style: { fontSize: '10px', fontWeight: 600, color: '#94a3b8' },
      },
      labels: {
        style: { fontSize: '9.5px', colors: '#94a3b8' },
        rotate: -25,
        trim: true,
      },
    };
    apexOptions.yaxis = {
      title: {
        text: isHorizontal ? (xAxisLabel || '') : (yAxisLabel || ''),
        style: { fontSize: '10px', fontWeight: 600, color: '#94a3b8' },
      },
      labels: { style: { fontSize: '9.5px', colors: '#94a3b8' } },
    };
  }

  return (
    <div className="w-full h-full min-h-[260px]">
      <Chart
        key={`preview-${chartType}-${apexType}-${rawRows.length}-${rawColumns.length}-${queryResult?.execution_time_ms || ''}`}
        options={apexOptions}
        series={series}
        type={apexType}
        height="100%"
        width="100%"
      />
    </div>
  );
}

// ─── Main AddWidgetModal Component ───
export default function AddWidgetModal({
  isOpen,
  onClose,
  onSubmit,
  initialData,
}: AddWidgetModalProps) {
  const [name, setName] = useState("");
  const [chartType, setChartType] = useState("bar");
  const [sources, setSources] = useState<any[]>([]);
  const [caps, setCaps] = useState<any>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);

  // Visualization config state for Axis Labels
  const [xAxisLabel, setXAxisLabel] = useState("");
  const [yAxisLabel, setYAxisLabel] = useState("");
  const [gaugeLabel, setGaugeLabel] = useState("");

  // Table mode state: "raw" (Detail Table) vs "aggregated" (Grouped Table)
  const [tableMode, setTableMode] = useState<"raw" | "aggregated">("raw");

  // Widget query definition state
  const [queryDef, setQueryDef] = useState<any>({
    source: "",
    joins: [],
    metrics: [{ type: "count", alias: "count" }],
    x_axis: null,
    split_series: null,
    columns: [],
    filters: [],
    limit: 100,
  });

  const [relatedSources, setRelatedSources] = useState<any[]>([]);
  const [previewResult, setPreviewResult] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [debugSql, setDebugSql] = useState(false);
  const [showSqlPane, setShowSqlPane] = useState(false);
  const [savingWidget, setSavingWidget] = useState(false);

  // Parse and populate initialData on open / change
  useEffect(() => {
    if (!isOpen) return;

    const wName = initialData?.name || initialData?.title || initialData?.chartTitle || "";
    setName(wName);

    const cType = initialData?.chart_type || initialData?.chartType || initialData?.widget_type || "bar";
    setChartType(cType);

    let vis = initialData?.visualization_config;
    if (typeof vis === "string") {
      try {
        vis = JSON.parse(vis);
      } catch {}
    }
    setXAxisLabel(vis?.x_axis_label || initialData?.xAxisLabel || "");
    setYAxisLabel(vis?.y_axis_label || initialData?.yAxisLabel || "");
    setGaugeLabel(vis?.gauge_label || vis?.label || "");

    let qDef: any = null;
    if (initialData?.query_definition) {
      if (typeof initialData.query_definition === "string") {
        try {
          qDef = JSON.parse(initialData.query_definition);
        } catch {}
      } else {
        qDef = JSON.parse(JSON.stringify(initialData.query_definition));
      }
    }

    if (!qDef) {
      // Legacy fallback
      const src = initialData?.dataSource || initialData?.dataset_name || "";
      const metricField = initialData?.metric_field || initialData?.yAxisField || "";
      const agg = initialData?.aggregation || initialData?.yAxisAggregation || "count";
      const groupField = initialData?.xAxisField || initialData?.group_by_field || initialData?.dimensions?.[0] || "";

      qDef = {
        source: src,
        joins: initialData?.joins || [],
        metrics: [{ type: agg, field: metricField, alias: "count" }],
        x_axis: groupField ? { type: "terms", field: groupField, limit: 10 } : null,
        split_series: null,
        columns: initialData?.columns || [],
        filters: Array.isArray(initialData?.filters) ? initialData.filters : [],
        limit: cType === "table" ? 50 : 100,
      };
    }

    setQueryDef({
      source: qDef?.source || "",
      joins: qDef?.joins || [],
      metrics: qDef?.metrics || [{ type: "count", alias: "count" }],
      x_axis: qDef?.x_axis || null,
      split_series: qDef?.split_series || null,
      columns: qDef?.columns || [],
      filters: qDef?.filters || [],
      limit: qDef?.limit !== undefined ? qDef.limit : (cType === "table" ? 50 : 100),
    });

    if (cType === "table") {
      if (Array.isArray(qDef?.columns) && qDef.columns.length > 0) {
        setTableMode("raw");
      } else if (qDef?.x_axis || (Array.isArray(qDef?.metrics) && qDef.metrics.length > 0)) {
        setTableMode("aggregated");
      } else {
        setTableMode("raw");
      }
    }
  }, [isOpen, initialData]);

  // Load API Capabilities & Datasources list
  const loadConfig = async () => {
    setLoadingConfig(true);
    try {
      const [srcRes, capsRes] = await Promise.all([
        baseapi.get("/cdash/meta/sources"),
        baseapi.get("/cdash/meta/capabilities"),
      ]);
      setSources(srcRes.data);
      setCaps(capsRes.data);

      if (!queryDef.source && srcRes.data.length > 0) {
        setQueryDef((prev: any) => ({ ...prev, source: srcRes.data[0].source }));
      }
    } catch (e: any) {
      showError(e.message || "Failed to load builder configurations");
    } finally {
      setLoadingConfig(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadConfig();
    }
  }, [isOpen]);

  // Load related tables/joins when source shifts
  const loadRelated = async () => {
    if (!queryDef.source) return;
    try {
      const res = await baseapi.get(`/cdash/meta/sources/${queryDef.source}/related`);
      setRelatedSources(res.data);
    } catch {
      setRelatedSources([]);
    }
  };

  useEffect(() => {
    if (isOpen && queryDef.source) {
      loadRelated();
    }
  }, [isOpen, queryDef.source]);

  // Combined picker list: main source + joins
  const pickerSourcesList = useMemo(() => {
    const prim = sources.find((s) => s.source === queryDef.source);
    const list = [{ source: queryDef.source, label: (prim?.label || queryDef.source) + " (primary)", prefix: "" }];
    (queryDef.joins || []).forEach((j: any) => {
      const meta = sources.find((s) => s.source === j.source);
      list.push({ source: j.source, label: meta?.label || j.source, prefix: j.source + "." });
    });
    return list;
  }, [sources, queryDef.source, queryDef.joins]);

  const activeCap = () => {
    if (!caps) return { metrics: [0, 10], buckets: [0, 2], split_series: false };
    return caps.chart_types[chartType] || { metrics: [0, 10], buckets: [0, 2], split_series: false };
  };

  // Build clean query def payload to send to query execute API
  const buildQueryPayload = () => {
    const out = JSON.parse(JSON.stringify(queryDef));
    if (chartType === "table" && tableMode === "raw") {
      out.metrics = [];
      out.x_axis = null;
      out.split_series = null;
      out.columns = Array.isArray(out.columns) ? out.columns : [];
    } else {
      out.columns = [];
      out.metrics = (out.metrics || []).filter((m: any) => m.type === "count" || m.field);
      if (out.x_axis && !out.x_axis.field) out.x_axis = null;
      if (out.split_series && !out.split_series.field) out.split_series = null;
      if (["metric", "gauge"].includes(chartType)) {
        out.x_axis = null;
        out.split_series = null;
        out.metrics = out.metrics.slice(0, 1);
      }
      if (["pie", "donut"].includes(chartType)) {
        out.split_series = null;
        out.metrics = out.metrics.slice(0, 1);
      }
      if (chartType === "heatmap") out.metrics = out.metrics.slice(0, 1);
      if (out.x_axis?.type === "date_histogram" || out.split_series) out.limit = Math.max(out.limit || 100, 500);
    }
    out.filters = (out.filters || []).filter(
      (f: any) => f.field && (f.value !== "" || ["exists", "not_exists"].includes(f.operator))
    );
    return out;
  };

  // Preview query definition execution
  const executePreview = async () => {
    if (!queryDef.source) return;
    setPreviewLoading(true);
    setPreviewError("");
    try {
      const query = buildQueryPayload();
      const params: any = { debug: debugSql };
      const storedLevel = localStorage.getItem("level");
      const levelObj = storedLevel ? JSON.parse(storedLevel) : null;
      if (levelObj && levelObj.name !== "global" && levelObj.id) {
        params.client_id = levelObj.id;
        query.client_id = levelObj.id;
      }
      const res = await baseapi.post("/cdash/query", query, {
        params,
      });
      setPreviewResult(res.data);
    } catch (e: any) {
      setPreviewError(e.response?.data?.detail ? JSON.stringify(e.response.data.detail) : e.message || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && initialData && (initialData.query_definition || initialData.dataSource || initialData.dataset_name)) {
      executePreview();
    }
  }, [isOpen]);

  // Save Widget details
  const handleSaveWidget = async () => {
    const widgetName = name.trim();
    if (!queryDef.source) {
      showError("Please select a data source");
      return;
    }

    setSavingWidget(true);
    try {
      let existingVis: Record<string, any> = {};
      if (initialData?.visualization_config) {
        if (typeof initialData.visualization_config === "string") {
          try {
            existingVis = JSON.parse(initialData.visualization_config);
          } catch {}
        } else if (typeof initialData.visualization_config === "object") {
          existingVis = initialData.visualization_config;
        }
      }

      const isAxisChart = !["metric", "gauge", "pie", "donut", "table"].includes(chartType);
      const visConfig: Record<string, any> = { ...existingVis };
      if (isAxisChart && xAxisLabel.trim()) {
        visConfig.x_axis_label = xAxisLabel.trim();
      } else {
        delete visConfig.x_axis_label;
      }
      if (isAxisChart && yAxisLabel.trim()) {
        visConfig.y_axis_label = yAxisLabel.trim();
      } else {
        delete visConfig.y_axis_label;
      }
      if (chartType === "gauge" && gaugeLabel.trim()) {
        visConfig.gauge_label = gaugeLabel.trim();
        visConfig.label = gaugeLabel.trim();
      } else if (chartType === "gauge") {
        delete visConfig.gauge_label;
        delete visConfig.label;
      }

      const queryPayload = buildQueryPayload();

      const payload: AddWidgetPayload = {
        name: widgetName,
        title: widgetName,
        chart_type: chartType,
        widget_type: chartType,
        query_definition: queryPayload,
        visualization_config: visConfig,
        previewResult: previewResult,
        // Compatibility Aliases
        chartType: chartType,
        chartTitle: widgetName,
        dataSource: queryDef.source,
        dataset_name: queryDef.source,
        aggregation: queryDef.metrics?.[0]?.type || "count",
        metric_field: queryDef.metrics?.[0]?.field || "",
        yAxisField: queryDef.metrics?.[0]?.field || "",
        xAxisField: queryDef.x_axis?.field || "",
        group_by_field: queryDef.x_axis?.field || "",
        dimensions: queryDef.x_axis?.field ? [queryDef.x_axis.field] : [],
        group_by_fields: queryDef.x_axis?.field ? [queryDef.x_axis.field] : [],
        columns: queryDef.columns || [],
        filters: queryDef.filters || [],
      };

      await onSubmit(payload);
      showSuccess("Widget configured successfully");
      onClose();
    } catch (e: any) {
      showError(e.message || "Failed to save widget");
    } finally {
      setSavingWidget(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl max-w-6xl w-full max-h-[92vh] flex flex-col overflow-hidden shadow-2xl animate-fade-in text-gray-100">
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-gray-800 px-6 py-4 bg-gray-850/60">
          <h2 className="text-sm font-bold text-white">{initialData ? "Edit Widget" : "Configure Widget"}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-700 text-gray-400 hover:text-white rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        {loadingConfig ? (
          <div className="flex-1 flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
            <p className="text-xs text-gray-455 uppercase tracking-widest font-semibold">Configuring environment...</p>
          </div>
        ) : !caps ? (
          <div className="flex-1 flex flex-col items-center justify-center py-20 gap-2 text-red-400 text-xs">
            Failed to load dynamic capabilities. Please check server connection.
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto custom-scroll p-6 grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-0 bg-gray-955">
            {/* Left Options Panel */}
            <div className="lg:col-span-5 flex flex-col gap-4 overflow-y-auto custom-scroll pr-1">
              {/* Step 1: Widget Title (Optional) */}
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex flex-col gap-2">
                <div className="flex justify-between items-center">
                  <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-450">1 · Widget Title</h3>
                  <span className="text-[10px] text-gray-500 font-medium">Optional</span>
                </div>
                <input
                  type="text"
                  placeholder="e.g. Active Agents by Hostname"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-gray-855 border border-gray-700 rounded-lg text-xs text-white px-3 py-2 focus:outline-none focus:border-orange-500 font-medium placeholder:text-gray-600"
                />
              </div>

              {/* Step 2: Datasource */}
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex flex-col gap-3">
                <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-450">2 · Data Source</h3>
                <CustomSelect
                  value={queryDef.source}
                  options={sources.map((s) => ({ value: s.source, label: s.label }))}
                  onChange={(val) => {
                    setQueryDef((prev: any) => ({
                      ...prev,
                      source: val,
                      joins: [],
                      metrics: [{ type: "count", alias: "count" }],
                      x_axis: null,
                      split_series: null,
                      filters: [],
                    }));
                    setPreviewResult(null);
                    setPreviewError("");
                  }}
                />

                {/* Joins row */}
                {(queryDef.joins || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {(queryDef.joins || []).map((j: any, i: number) => {
                      const meta = sources.find((s) => s.source === j.source);
                      return (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-gray-850 text-[10px] text-gray-300 border border-gray-700"
                        >
                          🔗 {meta?.label || j.source}
                          <button
                            type="button"
                            onClick={() => {
                              const updatedJoins = [...(queryDef.joins || [])];
                              updatedJoins.splice(i, 1);
                              setQueryDef((prev: any) => ({ ...prev, joins: updatedJoins }));
                            }}
                            className="text-gray-550 hover:text-red-400"
                          >
                            ✕
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Add join selector */}
                {relatedSources.length > 0 && (queryDef.joins || []).length < 3 && (
                  <div className="mt-1">
                    <CustomSelect
                      value=""
                      placeholder="＋ Add related table..."
                      options={relatedSources
                        .filter((r) => !(queryDef.joins || []).some((j: any) => j.source === r.source))
                        .map((r) => ({
                          value: `${r.source}|${r.join_field}`,
                          label: `${r.label}${r.cardinality === "one_to_many" ? " ⚠ (Many)" : ""}`,
                        }))}
                      onChange={(val) => {
                        if (!val) return;
                        const [src, jf] = val.split("|");
                        setQueryDef((prev: any) => ({
                          ...prev,
                          joins: [...prev.joins, { source: src, join_field: jf }],
                        }));
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Step 3: Chart Type */}
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex flex-col gap-3">
                <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-450">3 · Chart Type</h3>
                <div className="grid grid-cols-5 gap-2">
                  {Object.keys(caps?.chart_types || {}).map((ct) => {
                    const isSel = ct === chartType;
                    const border = isSel
                      ? "border-orange-500 bg-orange-500/10 text-orange-400 font-bold"
                      : "border-gray-800 bg-gray-855/40 text-gray-400 hover:border-gray-700";
                    const IconComponent = CHART_ICONS[ct] || Info;
                    const iconColor = CHART_COLORS[ct] || "text-gray-400";
                    return (
                      <button
                        type="button"
                        key={ct}
                        onClick={() => setChartType(ct)}
                        className={`flex flex-col items-center justify-center py-2.5 rounded-lg border text-[9px] uppercase tracking-wider text-center transition-all ${border}`}
                      >
                        <IconComponent className={`w-4 h-4 mb-1 ${iconColor}`} />
                        {ct.replace(/_/g, " ")}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Axis Labels (Visualization Config) */}
              {!["metric", "gauge", "pie", "donut", "table"].includes(chartType) && (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex flex-col gap-3">
                  <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-450">
                    Axis Labels
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">
                        X-Axis Label
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Severity"
                        value={xAxisLabel}
                        onChange={(e) => setXAxisLabel(e.target.value)}
                        className="bg-gray-855 border border-gray-700 rounded-lg text-xs text-white px-3 py-1.5 focus:outline-none focus:border-orange-500 font-medium"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">
                        Y-Axis Label
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Number of Vulns"
                        value={yAxisLabel}
                        onChange={(e) => setYAxisLabel(e.target.value)}
                        className="bg-gray-855 border border-gray-700 rounded-lg text-xs text-white px-3 py-1.5 focus:outline-none focus:border-orange-500 font-medium"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Gauge Label Config */}
              {chartType === "gauge" && (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex flex-col gap-3">
                  <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-450">
                    Gauge Config
                  </h3>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">
                      Gauge Label / Subtitle
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Total Vulnerabilities, Active Assets..."
                      value={gaugeLabel}
                      onChange={(e) => setGaugeLabel(e.target.value)}
                      className="bg-gray-855 border border-gray-700 rounded-lg text-xs text-white px-3 py-1.5 focus:outline-none focus:border-orange-500 font-medium"
                    />
                  </div>
                </div>
              )}

              {/* Mode Selector for Table Widgets */}
              {chartType === "table" && (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex flex-col gap-3">
                  <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-450">Table Display Mode</h3>
                  <div className="grid grid-cols-2 gap-2 bg-gray-955 p-1 rounded-xl border border-gray-800">
                    <button
                      type="button"
                      onClick={() => setTableMode("raw")}
                      className={`px-3 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                        tableMode === "raw"
                          ? "bg-orange-500/20 border border-orange-500/60 text-orange-400 shadow-md shadow-orange-500/10"
                          : "text-gray-400 hover:text-white hover:bg-gray-800/50"
                      }`}
                    >
                      <Table className="w-3.5 h-3.5" />
                      Raw Records (Detail Table)
                    </button>
                    <button
                      type="button"
                      onClick={() => setTableMode("aggregated")}
                      className={`px-3 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                        tableMode === "aggregated"
                          ? "bg-orange-500/20 border border-orange-500/60 text-orange-400 shadow-md shadow-orange-500/10"
                          : "text-gray-400 hover:text-white hover:bg-gray-800/50"
                      }`}
                    >
                      <BarChart2 className="w-3.5 h-3.5" />
                      Aggregated Summary (Grouped Table)
                    </button>
                  </div>
                </div>
              )}

              {/* Step 4 & Beyond */}
              {chartType === "table" && tableMode === "raw" ? (
                <MultiColumnSelector
                  getSourcesList={() => pickerSourcesList}
                  selectedColumns={queryDef.columns || []}
                  onChange={(cols) => setQueryDef((prev: any) => ({ ...prev, columns: cols }))}
                />
              ) : (
                <>
                  {/* Step 4: Metrics */}
                  {activeCap().metrics[1] > 0 && (
                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex flex-col gap-3">
                      <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-450">4 · Value (Measure)</h3>
                      {queryDef.metrics.map((m: any, idx: number) => (
                        <div key={idx} className="flex gap-2 items-center">
                          <div className="w-[38%]">
                            <CustomSelect
                              value={m.type}
                              options={caps.aggregations.map((agg: string) => ({
                                value: agg,
                                label: AGG_LABELS[agg] || agg,
                              }))}
                              onChange={(val) => {
                                const updated = [...queryDef.metrics];
                                updated[idx] = { ...updated[idx], type: val };
                                setQueryDef((prev: any) => ({ ...prev, metrics: updated }));
                              }}
                            />
                          </div>

                          {m.type !== "count" && (
                            <div className="flex-1">
                              <FieldPickerComponent
                                getSourcesList={() => pickerSourcesList}
                                value={m.field}
                                onPick={(f) => {
                                  const updated = [...queryDef.metrics];
                                  updated[idx] = { ...updated[idx], field: f };
                                  setQueryDef((prev: any) => ({ ...prev, metrics: updated }));
                                }}
                                roleFilter={
                                  ["sum", "avg", "median", "percentiles", "std_deviation"].includes(m.type)
                                    ? (fl) => ["integer", "float"].includes(fl.type)
                                    : null
                                }
                              />
                            </div>
                          )}

                          {queryDef.metrics.length > Math.max(1, activeCap().metrics[0]) && (
                            <button
                              type="button"
                              onClick={() => {
                                  const updated = [...queryDef.metrics];
                                  updated.splice(idx, 1);
                                  setQueryDef((prev: any) => ({ ...prev, metrics: updated }));
                              }}
                              className="p-1.5 hover:bg-gray-855 text-gray-400 hover:text-red-400 rounded-lg border border-gray-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                      {queryDef.metrics.length < activeCap().metrics[1] && (
                        <button
                          type="button"
                          onClick={() =>
                            setQueryDef((prev: any) => ({
                              ...prev,
                              metrics: [...prev.metrics, { type: "count", alias: `m_${prev.metrics.length}` }],
                            }))
                          }
                          className="text-left text-xs text-orange-400 hover:text-orange-300 font-bold mt-1"
                        >
                          ＋ Add another value measure
                        </button>
                      )}
                    </div>
                  )}

                  {/* Step 5: Group By */}
                  {activeCap().buckets[1] > 0 && (
                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex flex-col gap-3">
                      <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-450">
                        5 · Group By {["pie", "donut"].includes(chartType) ? "(Slices)" : "(X-Axis)"}
                      </h3>
                      <div className="flex gap-2">
                        <div className="w-[38%]">
                          <CustomSelect
                            value={queryDef.x_axis?.type || ""}
                            options={[
                              { value: "", label: "— none —" },
                              ...caps.bucket_types
                                .filter((b: string) => b !== "filters")
                                .map((b: string) => ({
                                  value: b,
                                  label: b.replace(/_/g, " "),
                                })),
                            ]}
                            onChange={(val) => {
                              if (!val) {
                                setQueryDef((prev: any) => ({ ...prev, x_axis: null }));
                              } else {
                                setQueryDef((prev: any) => ({
                                  ...prev,
                                  x_axis: { type: val, field: "", limit: 10, interval: "day" },
                                }));
                              }
                            }}
                          />
                        </div>

                        {queryDef.x_axis && (
                          <div className="flex-1">
                            <FieldPickerComponent
                              getSourcesList={() => pickerSourcesList}
                              value={queryDef.x_axis.field}
                              onPick={(f) =>
                                setQueryDef((prev: any) => ({
                                  ...prev,
                                  x_axis: { ...prev.x_axis, field: f },
                                }))
                              }
                              roleFilter={
                                queryDef.x_axis.type === "date_histogram"
                                  ? (fl) => ["datetime", "date"].includes(fl.type)
                                  : queryDef.x_axis.type === "histogram" || queryDef.x_axis.type === "range"
                                  ? (fl) => ["integer", "float"].includes(fl.type)
                                  : null
                              }
                            />
                          </div>
                        )}
                      </div>

                      {queryDef.x_axis?.type === "date_histogram" && (
                        <div className="flex justify-between items-center gap-4 mt-2">
                          <span className="text-[11px] text-gray-400">Date interval:</span>
                          <select
                            value={queryDef.x_axis.interval || "day"}
                            onChange={(e) =>
                              setQueryDef((prev: any) => ({
                                ...prev,
                                x_axis: { ...prev.x_axis, interval: e.target.value },
                              }))
                            }
                            className="bg-gray-855 border border-gray-700 rounded-lg text-xs text-white px-2.5 py-1 focus:outline-none"
                          >
                            {caps.time_intervals.map((t: string) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {queryDef.x_axis?.type === "terms" && (
                        <div className="flex justify-between items-center gap-4 mt-2">
                          <span className="text-[11px] text-gray-400">Limit top:</span>
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={queryDef.x_axis.limit || 10}
                            onChange={(e) =>
                              setQueryDef((prev: any) => ({
                                ...prev,
                                x_axis: { ...prev.x_axis, limit: parseInt(e.target.value) || 10 },
                              }))
                            }
                            className="w-20 bg-gray-855 border border-gray-700 rounded-lg text-xs text-white px-2.5 py-1 text-center font-mono focus:outline-none"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 6: Split by */}
                  {activeCap().split_series && queryDef.x_axis && (
                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex flex-col gap-3">
                      <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-450">6 · Split Series</h3>
                      <div className="flex gap-2 items-center">
                        <div className="flex-1">
                          <FieldPickerComponent
                            getSourcesList={() => pickerSourcesList}
                            value={queryDef.split_series?.field}
                            placeholder="— choose column to split —"
                            onPick={(f) =>
                              setQueryDef((prev: any) => ({
                                ...prev,
                                split_series: { type: "terms", field: f, limit: 8 },
                              }))
                            }
                            roleFilter={(fl) => fl.role !== "time"}
                          />
                        </div>
                        {queryDef.split_series && (
                          <button
                            type="button"
                            onClick={() => setQueryDef((prev: any) => ({ ...prev, split_series: null }))}
                            className="p-1.5 hover:bg-gray-855 text-gray-400 hover:text-red-400 rounded-lg border border-gray-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Limit Section for Table widgets */}
              {chartType === "table" && (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex flex-col gap-2.5">
                  <div className="flex items-center justify-between">
                    <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-450">Limit</h3>
                    <span className="text-[10px] text-gray-400">Max records to display</span>
                  </div>
                  <div className="flex gap-2 items-center">
                    <CustomSelect
                      value={String(queryDef.limit ?? 50)}
                      options={[
                        { value: "5", label: "5" },
                        { value: "10", label: "10" },
                        { value: "15", label: "15" },
                        { value: "25", label: "25" },
                        { value: "50", label: "50 (Default)" },
                        { value: "100", label: "100" },
                        { value: "1000", label: "All" },
                      ]}
                      onChange={(val) => {
                        setQueryDef((prev: any) => ({ ...prev, limit: parseInt(val) || 50 }));
                      }}
                    />
                  </div>

                  {(queryDef.limit > 25 || queryDef.limit === 1000) && (
                    <div className="flex items-start gap-2 p-2.5 bg-amber-500/10 border border-amber-500/30 rounded-lg text-[11px] text-amber-200">
                      <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                      <span>
                        <strong>Note:</strong> Selecting "All" or a large limit may cause the table to exceed the page boundaries if there is a lot of data. Please place the table with sufficient height or on a dedicated page so other widgets won't be affected.
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Step 6: Filters */}
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex flex-col gap-3">
                <h3 className="text-[10px] uppercase font-bold tracking-wider text-gray-450">Filters</h3>
                {queryDef.filters.map((f: any, idx: number) => (
                  <div key={idx} className="flex flex-col gap-2 bg-gray-850/40 border border-gray-700 p-2.5 rounded-lg relative">
                    <button
                      type="button"
                      onClick={() => {
                        const updated = [...queryDef.filters];
                        updated.splice(idx, 1);
                        setQueryDef((prev: any) => ({ ...prev, filters: updated }));
                      }}
                      className="absolute top-2 right-2 p-1 hover:bg-gray-700 text-gray-500 hover:text-red-400 rounded"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>

                    <div className="flex gap-2 pr-6">
                      <div className="flex-1">
                        <FieldPickerComponent
                          getSourcesList={() => pickerSourcesList}
                          value={f.field}
                          onPick={(v) => {
                            const updated = [...queryDef.filters];
                            updated[idx] = { ...updated[idx], field: v, value: "" };
                            setQueryDef((prev: any) => ({ ...prev, filters: updated }));
                          }}
                        />
                      </div>
                      <select
                        value={f.operator}
                        onChange={(e) => {
                          const updated = [...queryDef.filters];
                          updated[idx] = { ...updated[idx], operator: e.target.value };
                          setQueryDef((prev: any) => ({ ...prev, filters: updated }));
                        }}
                        className="bg-gray-855 border border-gray-700 rounded-lg text-xs text-white px-2 py-1.5 focus:outline-none w-[42%]"
                      >
                        {caps.filter_operators.map((op: string) => (
                          <option key={op} value={op}>
                            {OP_LABELS[op] || op}
                          </option>
                        ))}
                      </select>
                    </div>

                    {!["exists", "not_exists"].includes(f.operator) && (
                      (() => {
                        const { source: filterSource, field: filterField } = getFilterSourceAndField(f.field, queryDef);
                        return (
                          <FilterValueInput
                            source={filterSource}
                            field={filterField}
                            operator={f.operator}
                            value={f.value}
                            onChange={(val) => {
                              const updated = [...queryDef.filters];
                              updated[idx] = { ...updated[idx], value: val };
                              setQueryDef((prev: any) => ({ ...prev, filters: updated }));
                            }}
                          />
                        );
                      })()
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setQueryDef((prev: any) => ({
                      ...prev,
                      filters: [...prev.filters, { field: "", operator: "eq", value: "" }],
                    }))
                  }
                  className="text-left text-xs text-orange-400 hover:text-orange-300 font-bold mt-1"
                >
                  ＋ Add Query Filter
                </button>
              </div>
            </div>

            {/* Right Preview Panel */}
            <div className="lg:col-span-7 flex flex-col gap-4 min-h-0 bg-gray-900 border border-gray-800 rounded-xl p-5 overflow-hidden">
              <div className="flex items-center justify-between border-b border-gray-800 pb-3">
                <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Live Preview</span>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setDebugSql(!debugSql);
                      if (!debugSql) {
                        setShowSqlPane(true);
                      }
                    }}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-semibold transition-all ${
                      debugSql ? "bg-orange-600/15 border-orange-500 text-orange-400" : "bg-gray-800 border-gray-700 text-gray-400 hover:text-white"
                    }`}
                  >
                    <Code className="w-3.5 h-3.5" /> Debug SQL
                  </button>
                  <button
                    type="button"
                    onClick={executePreview}
                    disabled={previewLoading || !queryDef.source}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-750 text-white border border-gray-700 hover:border-gray-600 text-[11px] font-semibold rounded-lg transition-colors disabled:opacity-50"
                  >
                    {previewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-400" /> : <Eye className="w-3.5 h-3.5" />} Preview
                  </button>
                </div>
              </div>

              {/* Chart Rendering Preview Box */}
              <div className="flex-1 min-h-[300px] border border-gray-800 bg-gray-850/20 rounded-xl p-4 flex flex-col justify-center relative overflow-hidden">
                {previewLoading ? (
                  <div className="flex flex-col items-center justify-center gap-3">
                    <Loader2 className="w-7 h-7 text-orange-500 animate-spin" />
                    <p className="text-[11px] text-gray-500 uppercase tracking-widest font-semibold">Executing SQL query...</p>
                  </div>
                ) : previewError ? (
                  <div className="text-red-400 text-xs p-3 overflow-auto font-mono leading-relaxed bg-red-950/10 border border-red-950/20 rounded-lg">
                    {previewError}
                  </div>
                ) : previewResult ? (
                  <ApexChartModalPreview
                    chartType={chartType}
                    queryResult={previewResult}
                    xAxisLabel={xAxisLabel.trim()}
                    yAxisLabel={yAxisLabel.trim()}
                    gaugeLabel={gaugeLabel.trim()}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center text-gray-500 text-xs gap-1 opacity-70">
                    <Info className="w-5 h-5 text-gray-500 mb-1" />
                    <p>No preview generated.</p>
                    <p>Adjust data parameters and click "Preview" above.</p>
                  </div>
                )}
              </div>

              {/* SQL Debug details */}
              {debugSql && showSqlPane && previewResult?.sql && (
                <div className="bg-gray-855 border border-gray-800 rounded-xl p-3 flex flex-col gap-2 shrink-0">
                  <div className="flex justify-between items-center text-[10px] text-gray-400 uppercase tracking-wider font-semibold border-b border-gray-800 pb-1.5">
                    <span>Generated SQL Command</span>
                    <button type="button" onClick={() => setShowSqlPane(false)} className="text-gray-550 hover:text-white">
                      ✕
                    </button>
                  </div>
                  <pre className="text-[10px] text-orange-400/90 font-mono overflow-auto max-h-[120px] select-all leading-normal whitespace-pre-wrap">
                    {previewResult.sql}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-gray-800 px-6 py-4 bg-gray-850/30">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-800 border border-gray-700 hover:bg-gray-700 text-gray-300 hover:text-white text-xs font-semibold rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSaveWidget}
            disabled={savingWidget || loadingConfig}
            className="flex items-center gap-1.5 px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white text-xs font-semibold rounded-lg border border-orange-500 transition-colors shadow-lg shadow-orange-600/10 active:scale-[0.98] disabled:opacity-50"
          >
            {savingWidget ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            {initialData ? "Save Widget" : "Add Widget"}
          </button>
        </div>
      </div>
    </div>
  );
}
